import { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../utils/cookieUtils';

// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// ============================================
// Types
// ============================================

export interface Facility {
  id: string;
  name: string;
}

export interface Permissions {
  queries: string[];
  mutations: string[];
}

export interface CurrentUser {
  id: string;
  publicId: string | null;
  email: string;
  username: string | null;
  accountId: number | null;
  authMethod: 'password' | 'sso';
  ssoProvider: string | null;
  approved: boolean;
  roles: string[];
  permissions: Permissions;
  pages: string[];
  facilities: Facility[];
  signInCount: number | null;
  lastSignInAt: string | null;
}

export interface UseCurrentUserResult {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<CurrentUser | null>;
}

// ============================================
// GraphQL Query
// ============================================

const CURRENT_USER_QUERY = `
  query GetCurrentUser {
    currentUser {
      id
      publicId
      email
      username
      accountId
      authMethod
      ssoProvider
      approved
      roles
      permissions
      pages
      facilities {
        id
        name
      }
      signInCount
      lastSignInAt
    }
  }
`;

// ============================================
// Hook
// ============================================

/**
 * Hook to fetch the current authenticated user's full context.
 * Used primarily after SSO login to retrieve user data that isn't
 * available in the JWT token (roles, permissions, facilities, etc.)
 *
 * @param options.skip - Skip fetching on mount (default: false)
 * @param options.fetchOnMount - Whether to fetch immediately on mount (default: true)
 */
export const useCurrentUser = (options: { skip?: boolean; fetchOnMount?: boolean } = {}): UseCurrentUserResult => {
  const { skip = false, fetchOnMount = true } = options;

  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState<boolean>(!skip && fetchOnMount);
  const [error, setError] = useState<string | null>(null);

  const fetchCurrentUser = useCallback(async (): Promise<CurrentUser | null> => {
    const authToken = getAuthToken();

    if (!authToken) {
      setError('No authentication token found');
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: CURRENT_USER_QUERY
        })
      });

      if (response.status === 401) {
        setError('Unauthorized - please log in again');
        setUser(null);
        return null;
      }

      const result = await response.json();

      if (result.errors) {
        const errorMessage = result.errors[0]?.message || 'Failed to fetch user data';
        setError(errorMessage);
        setUser(null);
        return null;
      }

      if (!result.data?.currentUser) {
        setError('User not found');
        setUser(null);
        return null;
      }

      const currentUser = result.data.currentUser as CurrentUser;
      setUser(currentUser);
      return currentUser;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user data';
      console.error('Failed to fetch current user:', err);
      setError(errorMessage);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!skip && fetchOnMount) {
      fetchCurrentUser();
    }
  }, [skip, fetchOnMount, fetchCurrentUser]);

  return { user, loading, error, refetch: fetchCurrentUser };
};

// ============================================
// Standalone fetch function (for use outside React)
// ============================================

/**
 * Fetch current user data from the GraphQL API.
 * Can be used outside of React components.
 *
 * @returns Promise<CurrentUser | null>
 */
export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const authToken = getAuthToken();

  if (!authToken) {
    console.error('No authentication token found');
    return null;
  }

  try {
    const response = await fetch(GRAPHQL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        query: CURRENT_USER_QUERY
      })
    });

    if (response.status === 401) {
      console.error('Unauthorized - token may be invalid');
      return null;
    }

    const result = await response.json();

    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      return null;
    }

    return result.data?.currentUser || null;
  } catch (err) {
    console.error('Failed to fetch current user:', err);
    return null;
  }
}

export default useCurrentUser;
